import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Link2, Unplug } from 'lucide-react';
import { supabase } from '../../supabaseClient';
import { t, formatDate } from '../../i18n';
import { Card, Button, Alert, Badge, Spinner } from '../../components/ui';
import { callServer } from '../../lib/serverCall';

/**
 * Connecting the professional's own Mercado Pago (0074).
 *
 * The button asks the server for Mercado Pago's authorization page and goes
 * there; she approves on Mercado Pago, never typing a key here, and comes back
 * to this tab with ?mercadopago=connected or =error&motivo=…, read once and
 * then cleared from the address. What the app knows about her connection is
 * whether it exists and since when — the tokens stay sealed on the server.
 */
// Off until the practice's Mercado Pago application is registered and its keys
// are in Vercel: before that, the button could only fail. Turned on by setting
// REACT_APP_MERCADOPAGO_ENABLED=true in Vercel and redeploying.
const ENABLED = process.env.REACT_APP_MERCADOPAGO_ENABLED === 'true';

const MercadoPagoCard = () => (ENABLED ? <Connection /> : null);

const Connection = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [returned] = useState(() => {
    const query = new URLSearchParams(location.search);
    const outcome = query.get('mercadopago');
    return outcome ? { outcome, reason: query.get('motivo') } : null;
  });

  useEffect(() => {
    if (returned) navigate(location.pathname, { replace: true });
  }, [returned, navigate, location.pathname]);

  const load = useCallback(async () => {
    const { data, error: loadError } = await supabase.rpc('my_mercadopago');
    if (loadError) setError(loadError.message);
    else setStatus(data);
  }, []);

  useEffect(() => { load(); }, [load]);

  const connect = async () => {
    setBusy(true);
    setError(null);
    try {
      const { url } = await callServer('mercadopago-connect');
      window.location.assign(url);
    } catch (connectError) {
      setError(connectError.message);
      setBusy(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm(t('mercadopago.confirmDisconnect'))) return;
    setBusy(true);
    const { error: disconnectError } = await supabase.rpc('disconnect_mercadopago');
    setBusy(false);
    if (disconnectError) { setError(disconnectError.message); return; }
    load();
  };

  return (
    <Card title={t('mercadopago.title')} subtitle={t('mercadopago.hint')}>
      <div className="space-y-3">
        {returned?.outcome === 'connected' && <Alert tone="success">{t('mercadopago.connectedNow')}</Alert>}
        {returned?.outcome === 'error' && (
          <Alert tone="danger" title={t('mercadopago.connectFailed')}>{returned.reason}</Alert>
        )}
        {error && <Alert tone="danger">{error}</Alert>}

        {!status ? <Spinner /> : status.connected ? (
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="space-y-1">
              <Badge tone="success">{t('mercadopago.connected')}</Badge>
              <p className="text-xs text-slate-500">
                {t('mercadopago.since', { date: formatDate(status.connected_at) })}
                {!status.live_mode && ` · ${t('mercadopago.testAccount')}`}
              </p>
            </div>
            <Button variant="secondary" onClick={disconnect} loading={busy}>
              <Unplug size={16} />{t('mercadopago.disconnect')}
            </Button>
          </div>
        ) : (
          <div className="space-y-2">
            <Button onClick={connect} loading={busy}>
              <Link2 size={16} />{t('mercadopago.connect')}
            </Button>
            <p className="text-xs text-slate-500">{t('mercadopago.connectHint')}</p>
          </div>
        )}
      </div>
    </Card>
  );
};

export default MercadoPagoCard;
